// pages/JoinPage.jsx — Anamoria SPA
// v1.13 (2026-09-26) — TM notice in legal footer: ® → ™ and dropped "registered"
//   (mark is pending a USPTO Notice of Allowance, not registered; counsel-confirmed).
//   Footer now reads "Anamoria™ is a trademark of Anamoria Inc." Copy-only; no logic change.
// v1.12 (2026-09-12) — NEW-93: handleSignIn passes an opt-in login_hint (remembered
//   email) to pre-fill the Auth0 identifier screen. Additive; handleNameSubmit (new-user)
//   unchanged. Plan: Anamoria_Auth_EmailPrefill_LoginHint_Build_Plan_v0.2.
// v1.11 (2026-08-30) — item 4 #2 (revised): the v1.10 re-syncs still read a stale vv.height at
//   rest on iOS, so the bottom gap persisted. Fix the APPROACH: pin only while the keyboard is
//   open; at rest clear inline styles and let CSS height:100dvh size the page. Keyboard fix intact.
// v1.10 (2026-08-30) — item 4 #2: fix the initial viewport settle. The v1.9 pin sync ran once at
//   mount, before iOS settles the visual viewport, so the page loaded mis-sized (bottom gap /
//   shifted up) until a resize/scroll. Re-run sync on next frame + a 300ms delay, and on
//   pageshow/orientationchange. Presentation-only; the v1.9 keyboard pin behaviour is unchanged.
// v1.9 (July 14, 2026) — Mobile keyboard fix (VisualViewport pin).
//   Adds a useRef on .page + a useEffect that listens to window.visualViewport and
//   sizes/offsets the page box to the visible viewport when the iOS keyboard opens,
//   so the fixed wordmark can't be scrolled out of view. Keeps the F3 layout.
//   PRESENTATION-ONLY: no state, handler, API, route, sessionStorage, or copy change.
//   Reason: F3 CSS alone (v1.8) still shifted on a real iPhone (Mode A test, Jul 14).
//   Provenance: Anamoria_JoinPage_Mobile_Keyboard_Issue_Summary_v1.0 §7.3.
//   Pairs with JoinPage.module.css v1.8 (.page → position:fixed).
//
// v1.8 (July 13, 2026) — Onboarding UX redesign (Screens 1 & 2)
// STATUS: applied to branch redesign/joinpage-onboarding (cut from fresh origin/main
//         03e3fe2, v1.7/v1.6 confirmed). NOT yet merged, NOT deployed.
//         Pending before prod: staging E2E of both login paths; real support URL
//         (placeholder href="#support" below); CSP font-src check.
//
// What changed from v1.7 (PRESENTATION ONLY — no logic, state, API, or handler change):
//   - Replaced the two-column "brand panel / vault / 🔒 Secure" layout with the
//     settled single-column bone layout (prototype v0.11, AD-exact), wordmark-only.
//   - Copy swapped to the clinician-cleared v0.10 set (Anamoria_Onboarding_Copy_v0.10.md):
//       S1 headline "A quiet place to remember" / sub "A place to keep someone close";
//       code placeholder "The code your group gave you"; privacy line
//       "What you add here is private unless you choose to share it."; serif helper
//       "There's no rush, take all the time you need."; primary "Come in";
//       demoted "Already use Anamoria? Sign in"; support line
//       "If this is too much right now, you can find support here."
//       S2 badge = group name; headline "First, what should we call you?";
//       sub "Just your name, nothing more"; placeholder "Your name"; "Continue" / "Back".
//   - Removed every "vault" / "Secure" string and the RETURNING / JUST JOINING? labels.
//   - Support link is a PLACEHOLDER (href="#support") pending the real region-
//     appropriate URL (copy v0.10 Open item #1). Marked TODO.
//
// PRESERVED EXACTLY from v1.7 (do not touch without re-review):
//   - Imports, hooks, all state, the invite-token useEffect (auto-validate → step 2,
//     masked email), handleCodeSubmit → /pilot/validate-code, handleNameSubmit →
//     sessionStorage + loginWithRedirect({ max_age: 0 }), handleSignIn, the change
//     handlers, handleBackToCode, and every voice-passed error string.
//   - The persistent legal footer (Privacy Policy → new tab, © / ® notice).
//
// Design provenance: layout + copy per prototype v0.11 (AD "Hearth" spec) + copy v0.10.
// Background = §9.1 sage vignette + §9.2 paper grain, defined in the CSS module.
//
// Route: /join (public — no JWT, uses API key)
// Route: /join/invite/:token (public — no JWT, uses API key)

import { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams } from 'react-router-dom';
import { createApiClient } from '../api/client';
import { loginHintParams } from '../utils/rememberEmail';
import styles from './JoinPage.module.css';

export default function JoinPage() {
  const { loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const api = createApiClient(getAccessTokenSilently);

  // v1.6: Read invite token from URL path (/join/invite/:token)
  const { token: inviteToken } = useParams();

  // v1.3: Two-step flow — step 1 = code entry, step 2 = name entry
  const [step, setStep] = useState(inviteToken ? 0 : 1); // 0 = invite loading
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [groupName, setGroupName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // v1.5: Separate state for Sign In path (Returning section).
  const [signInError, setSignInError] = useState('');
  const [isSigningIn, setIsSigningIn] = useState(false);

  // v1.6: Invite token state
  const [inviteError, setInviteError] = useState('');
  const [inviteEmail, setInviteEmail] = useState(''); // masked email from validate

  // v1.9: Ref on the .page container for the VisualViewport keyboard pin (below).
  const pageRef = useRef(null);

  // v1.6: Auto-validate invite token on mount
  useEffect(() => {
    if (!inviteToken) return;

    let cancelled = false;

    async function validateInvite() {
      try {
        const data = await api.postPublic('/pilot/validate-invite', {
          token: inviteToken,
        });

        if (cancelled) return;

        sessionStorage.setItem('ana_groupId', data.groupId);
        sessionStorage.setItem('ana_groupName', data.groupName);
        sessionStorage.setItem('ana_inviteToken', inviteToken);

        setGroupName(data.groupName);
        setInviteEmail(data.maskedEmail);
        setStep(2); // Skip code entry — go straight to name
      } catch (err) {
        if (cancelled) return;

        if (err.error === 'INVITE_ALREADY_CLAIMED') {
          setInviteError("This invite has already been used. If you already have an account, sign in above.");
        } else if (err.error === 'INVITE_EXPIRED') {
          setInviteError("This invite has expired. Please ask for a new one.");
        } else if (err.error === 'GROUP_NOT_ACTIVE') {
          setInviteError("This group isn't active yet. Check back when your group leader lets you know it's ready.");
        } else {
          setInviteError("This invite link isn't working. Please check with the person who sent it.");
        }
        setStep(1); // Fall back to manual entry
      }
    }

    validateInvite();

    return () => { cancelled = true; };
  }, [inviteToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // v1.9: Mobile keyboard fix — pin the page box to the VisualViewport so the
  // fixed wordmark can't be scrolled out of view when iOS Safari opens the
  // on-screen keyboard. The F3 layout (masthead OUTSIDE the .scroll container)
  // handles the structure; this sizes the page to the *visible* viewport height
  // and offsets it to the viewport's top, so the keyboard resizes the scroll
  // area — not the header. Sanctioned iOS approach (VisualViewport API): F3 CSS
  // alone did not hold on device (Anamoria_JoinPage_Mobile_Keyboard_Issue_
  // Summary_v1.0 §7.3). Presentation-only — no state, handler, API, or copy change.
  useEffect(() => {
    const vv = window.visualViewport;
    const el = pageRef.current;
    if (!vv || !el) return; // graceful fallback: CSS height:100dvh container

    function sync() {
      // v1.11 (item 4 #2): only PIN to the visual viewport while the keyboard is OPEN.
      // At rest we clear the inline styles so CSS height:100dvh sizes the page — reading
      // vv.height at load was stale on iOS (address bar not settled), which is what left
      // the bottom gap. dvh is computed correctly for layout, so no gap; the pin still
      // engages for the keyboard case (v1.9 intent preserved).
      const keyboardOpen = (window.innerHeight - vv.height) > 100;
      if (keyboardOpen) {
        el.style.height = `${vv.height}px`;
        el.style.transform = `translateY(${vv.offsetTop}px)`;
      } else {
        el.style.height = '';
        el.style.transform = '';
      }
    }

    sync();
    // v1.10 (item 4 #2): iOS reports an unsettled viewport at mount (address-bar
    // animation in flight), so the single mount sync captured a stale height/offset —
    // the page ended up mis-sized, leaving a white gap at the bottom until the first
    // resize/scroll ("pull down to fix"). Re-run the same sync after the viewport
    // settles (next frame + a short delay) and on the events that signal a settle.
    requestAnimationFrame(sync);
    const settleTimer = setTimeout(sync, 300);
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    window.addEventListener('pageshow', sync);         // bfcache restore
    window.addEventListener('orientationchange', sync);

    return () => {
      clearTimeout(settleTimer);
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
      window.removeEventListener('pageshow', sync);
      window.removeEventListener('orientationchange', sync);
      el.style.height = '';
      el.style.transform = '';
    };
  }, []);

  // Step 1: Validate access code (Just Joining section)
  async function handleCodeSubmit(e) {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;

    setError('');
    setLoading(true);

    try {
      const data = await api.postPublic('/pilot/validate-code', {
        accessCode: trimmed,
      });

      sessionStorage.setItem('ana_groupId', data.groupId);
      sessionStorage.setItem('ana_groupName', data.groupName);

      // v1.3: Transition to name step instead of immediate Auth0 redirect
      setGroupName(data.groupName);
      setLoading(false);
      setStep(2);
    } catch (err) {
      // v1.4: Voice pass on error strings
      if (err.error === 'INVALID_CODE') {
        setError("That code doesn't look right. Check with your group leader.");
      } else if (err.error === 'GROUP_NOT_ACTIVE') {
        setError("This group isn't active yet. Check back when your group leader lets you know it's ready.");
      } else if (err.error === 'NETWORK_ERROR') {
        setError("Something's wrong with the connection. Please try again in a moment.");
      } else {
        setError("Something's not working right. Please try again, or let us know if it keeps happening.");
      }
      setLoading(false);
    }
  }

  // Step 2: Save name and redirect to Auth0
  async function handleNameSubmit(e) {
    e.preventDefault();
    const trimmed = displayName.trim();
    if (!trimmed) return;

    // Store name in sessionStorage — App.jsx bootstrap reads it for POST /pilot/join
    sessionStorage.setItem('ana_displayName', trimmed);

    // v1.3: max_age: 0 forces fresh Auth0 login even if a previous user's session
    // is cached in this browser.
    await loginWithRedirect({
      authorizationParams: { max_age: 0 },
      appState: { returnTo: '/' },
    });
  }

  // v1.5: Returning user sign-in with loading state.
  async function handleSignIn() {
    setSignInError('');
    setIsSigningIn(true);

    try {
      await loginWithRedirect({
        authorizationParams: { max_age: 0, ...loginHintParams() },
        appState: { returnTo: '/' },
      });
    } catch (err) {
      setIsSigningIn(false);
      setSignInError("Something's not working right. Please try again, or let us know if it keeps happening.");
    }
  }

  function handleCodeChange(e) {
    setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''));
    if (error) setError('');
  }

  function handleNameChange(e) {
    setDisplayName(e.target.value);
  }

  // Go back to step 1
  function handleBackToCode() {
    setStep(1);
    setError('');
    setInviteError('');
    setInviteEmail('');
    // Clear invite token from sessionStorage if going back
    sessionStorage.removeItem('ana_inviteToken');
  }

  return (
    <div className={styles.page} ref={pageRef}>
      {/* Fixed wordmark chrome — sits OUTSIDE the scroll area (F3 sticky-header pattern,
          Anamoria_F3_Sticky_Header_Session_Log §4-6) so the mobile keyboard can't scroll
          it out of view. Wordmark-only (butterfly stays for in-app). */}
      <header className={styles.masthead}>
        <div className={styles.word}>Anamoria</div>
      </header>

      {/* Scroll container: flex:1; overflow-y:auto; min-height:0. Only this scrolls when
          the keyboard opens — the wordmark above stays put. */}
      <main className={styles.scroll} role="main">
        <div className={styles.screen}>

        {/* ═══════════════════════════════════════
            STEP 0: Invite token loading (v1.6 — logic preserved)
            ═══════════════════════════════════════ */}
        {step === 0 && (
          <div className={styles.body}>
            <p className={styles.inviteLoadingText}>Checking your invite…</p>
          </div>
        )}

        {/* ═══════════════════════════════════════
            STEP 1: Code entry (single-column, copy v0.10)
            ═══════════════════════════════════════ */}
        {step === 1 && (
          <div className={styles.body}>
            {/* v1.6: Invite error banner — token validation failed, fell back to Step 1. */}
            {inviteError && (
              <div className={styles.inviteErrorBanner} role="alert">
                <p>{inviteError}</p>
              </div>
            )}

            <div className={styles.head}>
              <h1 className={styles.h1}>A quiet place to remember</h1>
              <p className={styles.sub}>A place to keep someone close</p>
            </div>

            <form onSubmit={handleCodeSubmit} className={styles.action} noValidate>
              <input
                id="access-code"
                type="text"
                inputMode="text"
                autoCapitalize="characters"
                autoCorrect="off"
                autoComplete="off"
                spellCheck={false}
                maxLength={20}
                value={code}
                onChange={handleCodeChange}
                placeholder="The code your group gave you"
                className={styles.input}
                aria-label="The code your group gave you"
                aria-describedby={error ? 'code-error' : undefined}
                disabled={loading}
              />

              <p className={styles.privacy}>
                What you add here is private unless you choose to share it.
              </p>

              {error && (
                <p id="code-error" className={styles.error} role="alert">
                  {error}
                </p>
              )}

              <button
                type="submit"
                className={styles.btn}
                disabled={loading || code.trim().length === 0}
              >
                {loading ? (
                  <span className={styles.btnSpinner} aria-label="Checking code" />
                ) : (
                  'Come in'
                )}
              </button>

              {/* Demoted returning-user path — preserves handleSignIn. */}
              <button
                type="button"
                className={styles.link}
                onClick={handleSignIn}
                disabled={isSigningIn}
              >
                {isSigningIn
                  ? 'One moment…'
                  : <>Already use Anamoria? <u>Sign in</u></>}
              </button>

              {signInError && (
                <p className={styles.error} role="alert">{signInError}</p>
              )}
            </form>
          </div>
        )}

        {/* ═══════════════════════════════════════
            STEP 2: Name entry (single-column, copy v0.10)
            ═══════════════════════════════════════ */}
        {step === 2 && (
          <div className={styles.body}>
            <div className={styles.head}>
              <span className={styles.badge}>{groupName}</span>
              <h1 className={styles.h1}>First, what should we call you?</h1>
              <p className={styles.sub}>Just your name, nothing more</p>
              {/* v1.6: Masked email hint — confirms which email the invite is for. */}
              {inviteEmail && (
                <p className={styles.inviteEmailHint}>Invite for {inviteEmail}</p>
              )}
            </div>

            <form onSubmit={handleNameSubmit} className={styles.action} noValidate>
              <input
                id="display-name"
                type="text"
                autoCapitalize="words"
                autoCorrect="off"
                autoComplete="name"
                spellCheck={false}
                maxLength={100}
                value={displayName}
                onChange={handleNameChange}
                placeholder="Your name"
                className={styles.input}
                aria-label="Your name"
              />

              <button
                type="submit"
                className={styles.btn}
                disabled={displayName.trim().length === 0}
              >
                Continue
              </button>

              <button
                type="button"
                className={styles.link}
                onClick={handleBackToCode}
              >
                <u>Back</u>
              </button>
            </form>
          </div>
        )}

        {/* ─── Persistent, quiet support line (copy v0.10) — every step.
            TODO: replace href with the real region-appropriate support URL
            (copy v0.10 Open item #1). Placeholder for now. ─── */}
        <footer className={styles.support}>
          <div className={styles.rule} />
          <p>
            If this is too much right now, you can{' '}
            <a href="#support"><u>find support here</u></a>.
          </p>
        </footer>

        {/* v1.7: Persistent legal footer — Privacy Policy (new tab so entered
            code/name isn't lost) + trademark/copyright notice. Preserved. */}
        <p className={styles.legalFooter}>
          <span className={styles.legalNotice}>
            © 2026 Anamoria Inc · Anamoria™ is a trademark of Anamoria Inc. ·{' '}
          </span>
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.legalLink}
          >
            Privacy Policy
          </a>
        </p>

        </div>
      </main>
    </div>
  );
}
